import { makeThemedStyles } from "@qpr/client-api-base/viewcomponents/utils";
import flattenHierarchy from "backend/flattenHierarchy";
import { ICardElementWithChildren } from "backend/model/model";
import React from "react";
import HighChart from "./Highchart";
import { IChartSelectEvent } from "./types";

const useDonutKpiInitiativeStyles = makeThemedStyles(
  "DonutKpiInitiative",
  (theme) => ({
    container: {
      display: "flex",
      width: "100%",
      alignItems: "center",
      justifyContent: "center",
    },
    chartTitleStyle: {
      fontFamily: "Roboto, Helvetica",
      fontSize: "18px",
      fontWeight: 500,
      color: "rgb(84,84,84)",
    },
    legend: {
      background: "rgba(200,200,200,0.2)",
      color: "black",
      padding: "8px 15px 8px 15px",
      borderRadius: "10px",
      zIndex: 1000,
      transform: "translateY(-20px)",
    },
  })
);

export const DonutKpiInitiative = React.memo(
  (
    props: React.PropsWithChildren<{
      element: ICardElementWithChildren[];
      perfNameKey: keyof ICardElementWithChildren;
      perfColorKey: keyof ICardElementWithChildren;
      typenames: string[];
      width?: number;
      chartType?: string;
      height?: string | number;
      onSelect?: (value: IChartSelectEvent) => void;
      disableAnimation?: boolean;
    }>
  ) => {
    const styles = useDonutKpiInitiativeStyles();
    const { element: el, perfNameKey, perfColorKey, typenames } = props;
    const list = React.useMemo(() => {
      return flattenHierarchy(el).filter((el) =>
        props.typenames.includes(el.typename)
      );
    }, [el]);

    const rangeNames: { name: string; color: string; cnt: number }[] = [];
    const rangeStyles: any = {};
    // range names for each type
    typenames.forEach((value) => {
      list
        .filter(
          (el) => el.typename.toLocaleLowerCase() === value.toLocaleLowerCase()
        )
        .forEach((item) => {
          const rangeName = item[perfNameKey] || "No Value";
          const rangeColor = item[perfColorKey] || "000000";
          if (rangeName) {
            if (!rangeNames.find((r) => r.name === rangeName)) {
              rangeNames.push({
                name: rangeName as string,
                color: rangeColor as string,
                cnt: 1,
              });
              rangeStyles[
                `--range-${rangeName}`
              ] = `linear-gradient(to right, #${rangeColor}11, #${rangeColor}22, #${rangeColor}11)`;
            } else {
              const r = rangeNames.find((r) => r.name === rangeName);
              if (r) {
                r.cnt += 1;
              }
            }
          }
        });
    });

    //     for (let i = 0; i < list; i++) {}

    const hcOptions = React.useMemo(() => {
      if (!el) {
        return {};
      }

      const dataList: any[] = [];

      console.log("rangeNames", rangeNames, list);

      // x / N = 2 / 360
      // => x = N * 2 / 360
      rangeNames.forEach((tn) => {
        dataList.push({
          name: tn.name,
          events: {
            click: function (this: any, event: any) {
              if (props.onSelect) {
                props.onSelect({
                  category: event.point.category,
                  index: this.index,
                  name: this.name,
                  items: props.element,
                });
              }
            },
          },
          color: {
            linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
            stops: [
              [0, `#${tn.color || "gray"}ff`],
              [1, `#${tn.color || "gray"}66`],
            ],
          },
          y: tn.cnt,
        });
        dataList.push({
          visible: false,
          y: list.length > 1 ? (3 * list.length) / 360 : 0,
        });
      });
      return {
        credits: {
          enabled: false,
        },
        chart: {
          type: "pie",
          width: 400,
          height: 420,
          backgroundColor: "rgba(255,255,255,0)",
          renderTo: "container",
        },
        title: {
          useHTML: true,
          verticalAlign: "middle",
          floating: true,
          text: `<div class="${styles.container}"><div class="${styles.chartTitleStyle}">${list.length}</div></div>`,
        },
        xAxis: {
          gridLineColor: "transparent",
          categories: typenames,
        },
        yAxis: {
          gridLineColor: "transparent",
          min: 0,
          title: {
            text: "",
          },
        },
        plotOptions: {
          series: {
            animation: !props.disableAnimation,
          },
          pie: {
            /*
            borderWidth: 2,
            borderColor: undefined,
            */
            size: "50%",
            center: ["50%", "50%"],
            dataLabels: {
              crop: false,
              overflow: "allow",
              distance: 20,
              connectorWidth: 0,
              useHTML: true,
              formatter: function (this: any) {
                console.log("this.point", this.point);
                return `<div class="${styles.legend}" style="background:var(--range-${this.point.name});"><div class="">${this.point.name}</div><div>${this.point.y}</div></div>`;
              },
            },
          },
        },
        legend: {
          enabled: false,
          align: "left",
          x: 70,
          verticalAlign: "top",
          y: 70,
          floating: true,
          backgroundColor: "rgba(255,255,255,0)",
          borderColor: "#CCC",
          borderWidth: 1,
          shadow: false,
        },
        tooltip: {
          headerFormat: "<b>{point.x}</b><br/>",
          pointFormat: "{point.name}: {point.y}",
        },
        series: [
          {
            name: "Overview",
            innerSize: "60%",
            data: dataList,
            ignoreHiddenPoint: false,
          },
        ],
      };
    }, [el]);

    if (!el) return null;
    return (
      <div style={{ ...rangeStyles }}>
        <HighChart chartOptions={hcOptions} />
      </div>
    );
  }
);
